import React, { useState } from 'react';
import { LifeBuoy, Paperclip } from 'lucide-react';
import type { ThreadMessage } from '../../../services/supportApi';
import { formatShortDate, initialsOf } from '../constants';
import { Linkified } from '../linkify';

/**
 * Il thread pubblico arriva da Mongo: le voci hanno un `_id` proprio, che il
 * tipo condiviso non dichiara perche nessun'altra vista lo usa.
 */
type HistoryMessage = ThreadMessage & { _id?: string };

/**
 * Avatar istituzionale del canale: il logo UNASCA servito dal frontend
 * (`public/images/`, lo stesso dell'intestazione del sito). Gli operatori
 * restano mascherati, quindi qui NON va mai la foto di chi risponde.
 */
const CHANNEL_LOGO = '/images/logo-unasca.png';

interface CaseHistoryProps {
  messages: HistoryMessage[];
  /** Tipo di autore che corrisponde a «Tu». */
  mineAuthorType?: string;
  /** Nome del canale: il richiedente dialoga col canale, non col singolo operatore. */
  channelName?: string;
  /** URL assoluto della foto del richiedente; senza, restano le iniziali. */
  requesterPhotoUrl?: string;
  /**
   * Nome del richiedente per esteso: intesta la sua bolla e da le iniziali
   * dell'avatar. Senza, si ripiega su «Tu».
   */
  requesterName?: string;
  onOpenAttachment: (storedName: string, displayName?: string) => void;
  emptyText?: string;
}

/**
 * Cronologia della richiesta: voci datate in colonna, tutte allineate a
 * sinistra. Non e una chat — niente bolle contrapposte, niente campo aperto —
 * ma il registro di una pratica: le bolle delimitano domande e risposte e
 * l'avatar dice a colpo d'occhio chi parla: la foto del richiedente (o le sue
 * iniziali) da una parte, il logo UNASCA dall'altra. Mai la foto di chi
 * risponde: il richiedente dialoga col canale, non col singolo operatore.
 */
const CaseHistory: React.FC<CaseHistoryProps> = ({
  messages,
  mineAuthorType = 'associate',
  channelName = 'UNASCA',
  requesterPhotoUrl,
  requesterName,
  onOpenAttachment,
  emptyText = 'Nessuna comunicazione registrata',
}) => {
  // Un'immagine che non carica non deve lasciare un cerchio vuoto: si torna
  // alle iniziali (richiedente) o all'icona (canale).
  const [photoBroken, setPhotoBroken] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);

  if (!messages || messages.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">{emptyText}</p>
    );
  }

  // Chi parla: il nome per esteso quando c'e, «Tu» come ultima spiaggia.
  const mineName = requesterName?.trim() || 'Tu';
  // Le iniziali vengono dallo stesso nome (prime due parole). `initialsOf`
  // ripiega su "–": per un avatar e piu parlante "TU".
  const mineInitials = (() => {
    const from = initialsOf(mineName);
    return from && from !== '–' ? from : 'TU';
  })();

  return (
    <ol className="space-y-4">
      {messages.map((msg, idx) => {
        const isMine = msg.authorType === mineAuthorType;
        // I ticket nati da email portano l'authorName originale sul primo
        // messaggio: si tiene com'e, e gia la forma giusta.
        const who = isMine ? mineName : msg.authorName || channelName;
        return (
          <li key={msg._id ?? `${msg.sentAt}-${idx}`} className="flex gap-3">
            {isMine && requesterPhotoUrl && !photoBroken ? (
              <img
                src={requesterPhotoUrl}
                alt={`Foto di ${mineName}`}
                loading="lazy"
                onError={() => setPhotoBroken(true)}
                className="w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full object-cover bg-gray-100 dark:bg-gray-700"
              />
            ) : !isMine && !logoBroken ? (
              <img
                src={CHANNEL_LOGO}
                alt="UNASCA"
                loading="lazy"
                onError={() => setLogoBroken(true)}
                className="w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full object-contain bg-white dark:bg-gray-100 border border-gray-200 dark:border-gray-300 p-0.5"
              />
            ) : (
              <span
                aria-hidden="true"
                className={`w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${
                  isMine
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                }`}
              >
                {isMine ? mineInitials : <LifeBuoy className="w-4 h-4" />}
              </span>
            )}

            <div
              role="group"
              aria-label={`Messaggio di ${who} del ${formatShortDate(msg.sentAt)}`}
              className={`min-w-0 flex-1 rounded-2xl rounded-tl-md border border-gray-200 dark:border-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 ${
                isMine ? 'bg-indigo-50/60 dark:bg-indigo-900/20' : 'bg-white dark:bg-gray-800'
              }`}
            >
              {/* Nome e data: sotto i 640px la data va a capo (`basis-full`),
                  cosi un nome lungo non viene mai tagliato dalla data. */}
              <p className="flex flex-wrap items-baseline gap-x-1.5 text-xs min-w-0">
                <span
                  className="font-semibold text-gray-700 dark:text-gray-200 truncate min-w-0"
                  title={who}
                >
                  {who}
                </span>
                <span aria-hidden="true" className="hidden sm:inline text-gray-400 dark:text-gray-500">
                  ·
                </span>
                <span className="basis-full sm:basis-auto text-gray-500 dark:text-gray-400 tabular-nums">
                  {formatShortDate(msg.sentAt)}
                </span>
                {/* Testo corretto dopo l'invio: al richiedente basta sapere CHE e
                    stato modificato. Chi l'ha fatto e il testo precedente non
                    escono dall'API (toRequesterView li cancella). */}
                {msg.editedAt && (
                  <span className="italic text-gray-400 dark:text-gray-500">modificato</span>
                )}
              </p>
              {msg.body && (
                <p className="mt-1 text-sm leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                  <Linkified text={msg.body} />
                </p>
              )}
              {msg.attachments && msg.attachments.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {msg.attachments.map((att, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => onOpenAttachment(att.url, att.filename)}
                        className="inline-flex items-center gap-1.5 max-w-full px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/60 text-xs text-gray-700 dark:text-gray-200 hover:border-indigo-400 dark:hover:border-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                      >
                        <Paperclip className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{att.filename}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default CaseHistory;
